import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
  Typography,
  Divider,
  Chip,
} from '@mui/material';
import { FC } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import { useDeleteTaskMutation } from 'redux/apiSlices/tasks.slice';
import { ITaskUI } from 'data/types/task.type';
import { useSnackbar } from 'notistack';
import { PrivateRoutesPaths } from 'routes/Routes.types';
import { TRtkErrorType } from 'data/types/general.types';
import { errorMessages } from 'data/messages/messages';
import AlertDialogWithState from 'components/Dialogs/AlertDialog/AlertDialogWithState';
import TaskTitle from './TaskTitle';
import TaskAssignee from './TaskAssignee';
import TaskStatus from './TaskStatus';
import TaskDescription from './TaskDescription';
import TaskDuedate from './TaskDuedate';
import TaskEstimate from './TaskEstimate';
import TaskLabels from './TaskLabels';

interface IUpdateTaskModalProps {
  open: boolean;
  closeModal: () => void;
  task: ITaskUI;
}

const TaskInfoModal: FC<IUpdateTaskModalProps> = ({
  open,
  closeModal,
  task,
}) => {
  const [deleteTask, { isLoading }] = useDeleteTaskMutation();
  const { enqueueSnackbar } = useSnackbar();

  const onDelete = async () => {
    try {
      await deleteTask({
        taskId: task.taskId,
      }).unwrap();
      closeModal();
    } catch (err) {
      const error = err as TRtkErrorType;
      enqueueSnackbar(error?.data?.message || errorMessages.default, {
        variant: 'error',
      });
    }
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(
      `${window.location.origin}/${PrivateRoutesPaths.TaskDetails.replace(
        ':taskId',
        task.taskId
      )}`
    );
    enqueueSnackbar('Link copied to clipboard', { variant: 'success' });
  };

  return (
    <Dialog
      open={open}
      onClose={closeModal}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          p: 3,
          pb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              backgroundColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <AssignmentOutlinedIcon sx={{ color: 'white', fontSize: 24 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
            <TaskTitle taskId={task.taskId} title={task.title} />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 2 }}>
          <Tooltip title="Copy link">
            <IconButton
              size="small"
              onClick={copyLink}
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'primary.main' },
              }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <AlertDialogWithState
            title="Delete task"
            text={`Are you sure you want to delete "${task.title}"? This action cannot be undone.`}
            submitDisabled={isLoading}
            submitLoading={isLoading}
            onConfirm={onDelete}
          >
            <Tooltip title="Delete task">
              <IconButton
                size="small"
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'error.main' },
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </AlertDialogWithState>

          <IconButton
            size="small"
            onClick={closeModal}
            sx={{
              color: 'text.secondary',
              ml: 0.5,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Divider />

      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            minHeight: 500,
          }}
        >
          {/* Main Content - Description */}
          <Box
            sx={{
              flex: 1,
              p: 3,
              borderRight: { md: '1px solid' },
              borderColor: { md: 'divider' },
            }}
          >
            <TaskDescription
              taskId={task.taskId}
              description={task.description}
            />
          </Box>

          {/* Sidebar - Properties */}
          <Box
            sx={{
              width: { xs: '100%', md: 280 },
              flexShrink: 0,
              p: 3,
              backgroundColor: 'background.default',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                mb: 2,
                textTransform: 'uppercase',
                fontSize: '0.7rem',
                letterSpacing: '0.5px',
              }}
            >
              Properties
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {/* Status */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', fontWeight: 500, mb: 0.5, display: 'block' }}
                >
                  Status
                </Typography>
                <TaskStatus task={task} />
              </Box>

              {/* Assignee */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', fontWeight: 500, mb: 0.5, display: 'block' }}
                >
                  Assignee
                </Typography>
                <TaskAssignee
                  assignee={task.assignee}
                  projectId={task.projectId}
                  taskId={task.taskId}
                />
              </Box>

              {/* Labels */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', fontWeight: 500, mb: 0.5, display: 'block' }}
                >
                  Labels
                </Typography>
                <TaskLabels task={task} />
              </Box>

              {/* Due Date */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', fontWeight: 500, mb: 0.5, display: 'block' }}
                >
                  Due date
                </Typography>
                <TaskDuedate taskId={task.taskId} dueDate={task.dueDate} />
              </Box>

              {/* Estimate */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', fontWeight: 500, mb: 0.5, display: 'block' }}
                >
                  Estimate
                </Typography>
                <TaskEstimate taskId={task.taskId} estimate={task.estimate} />
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TaskInfoModal;
